import { combineReducers } from "redux";
import { connectRouter } from "connected-react-router";
import Settings from "./Settings";
import Auth from "./Auth";
import userReducer from "./User";
import Newsletter from "./Newsletter";
import Boards from "./Boards";
import Translations from "./Translations";
import WidgetSettings from "./WidgetSettings";
import Form from "./Form";
import Messages from "./Messages";
import Sidebar from "./Sidebar";
import Cart from "./Cart";
import FormsV2 from "./FormsV2";
import Companies from "./Companies";
import WidgetCache from "./WidgetCache";
import ChatSettings from "./ChatSettings";
import VisibilityOptions from "./VisibilityOptions";
import {
   AuthState,
   UserState,
   NewsletterState,
   BoardsState,
   TranslationsState,
   ChatSettingsState,
   WidgetCacheState,
   WidgetSettingsState,
   FormState,
   MessagesState,
   Formsv2State,
   CompaniesState,
   VisibilityOptionsState,
   SelectedRowsState,
   DevFormState,
} from "../reduxInterfaces/ReducersInterfaces";
import SelectedRows from "./SelectedRows";
import DevForm from "./DevForm";

const appReducer = (history) =>
   combineReducers({
      router: connectRouter(history),
      settings: Settings,
      auth: Auth,
      user: userReducer,
      newsletter: Newsletter,
      boards: Boards,
      translations: Translations,
      widgetSettings: WidgetSettings,
      chatSettings: ChatSettings,
      widgetCache: WidgetCache,
      form: Form,
      messages: Messages,
      sidebar: Sidebar,
      cart: Cart,
      formsv2: FormsV2,
      companies: Companies,
      visibilityOptions: VisibilityOptions,
      selectedRows: SelectedRows,
      devForm: DevForm,
   });

export interface DefaultRootState {
   auth: AuthState;
   user: UserState;
   newsletter: NewsletterState;
   boards: BoardsState;
   translations: TranslationsState;
   widgetSettings: WidgetSettingsState;
   chatSettings: ChatSettingsState;
   widgetCache: WidgetCacheState;
   form: FormState;
   messages: MessagesState;
   formsv2: Formsv2State;
   companies: CompaniesState;
   visibilityOptions: VisibilityOptionsState;
   selectedRows: SelectedRowsState;
   devForm: DevFormState;
}

const initialState: DefaultRootState = {
   auth: {
      loading: false,
      alertMessage: "",
      showMessage: false,
      initURL: "/boardManager",
      authUser: null,
      organization: { id: null, name: "" },
      currentCompany: { accessAs: null, companyDetailedName: "", companySimpleName: "" },
      currentDepartment: {
         defaultDepartment: { id: null, name: "" },
         currentDepartment: { id: null, name: "" },
      },
   },
   user: {
      id: null,
      oldId: 0,
      mainPageUrl: "",
      firstName: "",
      lastName: "",
      email: "",
      login: "",
      companies: [],
      phoneNumber: "",
      activated: false,
      langKey: null,
      timeZone: null,
      avatarUrl: null,
      avatarLink: null,
      legacyName: "",
      legacyHost: "",
      legacyApiController: "",
      legacyScope: "",
      platform: "",
      platformToken: "",
      currentLocation: null,
      isLoggedFirstTime: false,
      departments: [],
      sharedAccounts: [],
      nonAcceptedConsents: null,
      loggedFrom: null,
      sharedAccountsWithStatus: [],
   },
   newsletter: {
      newsletterContent: [],
      templateName: "",
      templateSubject: "",
      templateContent: "",
      templateSubscribers: [],
      templateSaveable: false,
      templateNewsletter: {
         name: "",
         id: null,
      },
      csv: null,
   },
   boards: {
      currentBoard: {
         backgroundColourHex: "",
         boardGroups: [],
         boardIconId: "",
         companyId: "",
         connections: {
            id: null,
            widgetId: "",
            actionButtonId: null,
            data: [],
         },
         deleted: false,
         deletionTime: "",
         id: null,
         members: [
            {
               id: 0,
               memberType: "",
               showInMainNav: null,
               member: {},
            },
         ],
         name: "",
         sheets: {
            id: null,
            name: "",
            priority: null,
            iconFileName: null,
            iconFileId: null,
         },
         tabsAreBottomOnMobile: null,
         translations: {
            category: null,
            key: "",
            values: {
               de: "",
               en: "",
               pl: "",
               sp: "",
            },
         },
      },
      boards: [],
      sheet: "",
      role: "",
      listOfWidgetsId: [],
      clickedRow: null,
      stateChange: false,
   },
   translations: {
      translations: [],
   },
   widgetSettings: {
      settings: [],
   },
   chatSettings: {
      chatButtonNamePL: "",
      chatButtonNameEN: "",
      chatBtnVisible: false,
      isChatOpen: false,
      isChatMinimized: true,
      isChatDocked: false,
      // ---- nowe ----
      fullScreenMode: false,
      currentRoomsListFolder: "Messages",
      currentRoom: { chatUsers: [], key: "", id: "", title: "", type: "" },
      selectedRoomId: "",
   },
   widgetCache: [],
   form: {
      defaultData: [],
      technames: {},
      gridId: "",
      projectList: [],
      clientId: {},
      formTabs: null,
      currentTabKey: 0,
      gridRequired: {},
   },
   messages: {
      actionId: null,
      mailBoxId: null,
      detailsMessage: null,
      comment: "",
      stepId: null,
      selectedRecipient: null,
      mailBoxes: [],
      listOfAllUsers: [],
      allUsersLoading: false,
      allUsersLoaded: false,
      allUsersError: null,
   },
   formsv2: { isReadonly: false, formInfo: {}, gridFormRefs: [], messageHistory: [], backUrl: "" },
   companies: { avatars: [] },
   visibilityOptions: {
      isForwardVisible: true,
      isAskQuestionVisible: true,
      isPlanVisible: true,
      isToDoVisible: true,
   },
   selectedRows: {
      selectedRowIds: [],
   },
   devForm: {
      isAnyChange: false,
   },
};

const rootReducer = (history) => (state, action) => {
   if (action.type === "SIGNOUT_USER_SUCCESS") {
      // If we want to persist some data after signout, we need to include it here
      state = {
         ...initialState,
         settings: {
            ...state.settings,
         },
         translations: {
            ...state.translations,
         },
         cart: {
            ...state.cart,
         },
         widgetCache: state.widgetCache,
      };
   }
   return appReducer(history)(state, action);
};

export default rootReducer;
